import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Support() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [deviceInfo, setDeviceInfo] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    const categoryLabel = {
      general: 'General Inquiry',
      technical: 'Technical Issue',
      billing: 'Billing & Subscription',
      account: 'Account Help',
      feature: 'Feature Request',
      bug: 'Bug Report',
    }[category];

    const mailSubject = encodeURIComponent(`ZEPHYR AI Support - ${categoryLabel}: ${subject}`);
    const mailBody = encodeURIComponent(
      `Name: ${name}\n` +
      `Email: ${email}\n` +
      `Category: ${categoryLabel}\n` +
      `Device & App Info: ${deviceInfo}\n\n` +
      `Subject: ${subject}\n\n` +
      `Message:\n${message}`
    );

    window.location.href = `mailto:help@zeynsoft.com?subject=${mailSubject}&body=${mailBody}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-dark-900">
      <nav className="fixed top-0 w-full z-50 bg-dark-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="ZEPHYR AI" className="w-10 h-10 rounded-2xl" />
            <span className="text-xl font-bold">ZEPHYR AI</span>
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition">
            ← Back to Home
          </Link>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
              <span className="text-sm text-primary font-medium">We're here to help</span>
            </div>
            <h1 className="text-5xl font-bold mb-6 text-white">Support Center</h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Get the help you need with ZEPHYR AI. Browse frequently asked questions, contact our team, or submit a support request.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <a
              href="mailto:help@zeynsoft.com"
              className="bg-dark-800 border border-white/10 rounded-xl p-6 hover:border-primary/50 transition group"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition">
                <span className="text-2xl">📧</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Email Support</h3>
              <p className="text-gray-400 text-sm mb-3">Reach our support team directly</p>
              <p className="text-primary text-sm font-medium">help@zeynsoft.com</p>
            </a>

            <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">⏱️</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Response Time</h3>
              <p className="text-gray-400 text-sm mb-3">We aim to reply within</p>
              <p className="text-primary text-sm font-medium">24 – 48 business hours</p>
            </div>

            <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <span className="text-2xl">🌍</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Working Hours</h3>
              <p className="text-gray-400 text-sm mb-3">Monday – Friday</p>
              <p className="text-primary text-sm font-medium">09:00 – 18:00 (GMT+3)</p>
            </div>
          </div>

          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-white">Contact Support</h2>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="bg-dark-800 border border-white/10 rounded-xl p-8">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-semibold mb-2">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-semibold mb-2">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="category" className="block text-white font-semibold mb-2">
                    Category <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="category"
                    required
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="technical">Technical Issue</option>
                    <option value="billing">Billing & Subscription</option>
                    <option value="account">Account Help</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="subject" className="block text-white font-semibold mb-2">
                    Subject <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    required
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Briefly describe your issue"
                    className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="deviceInfo" className="block text-white font-semibold mb-2">
                    Device & App Version
                  </label>
                  <input
                    type="text"
                    id="deviceInfo"
                    value={deviceInfo}
                    onChange={(e) => setDeviceInfo(e.target.value)}
                    placeholder="e.g., iPhone 15 Pro, iOS 17.4, App v1.2.0"
                    className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition"
                  />
                  <p className="text-gray-400 text-sm mt-2">
                    Helps us diagnose technical issues faster
                  </p>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-white font-semibold mb-2">
                    Message <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="6"
                    placeholder="Describe your issue in detail. Include steps to reproduce if reporting a bug."
                    className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 rounded-lg transition transform hover:scale-105"
                >
                  Send Support Request
                </button>

                <p className="text-gray-400 text-sm text-center mt-4">
                  This will open your email client with a pre-filled message
                </p>
              </form>
            ) : (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">✓</span>
                </div>
                <h3 className="text-2xl font-semibold mb-3 text-green-400">Email Client Opened</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Your email client should have opened with a pre-filled support request. Please review and send the email to complete your request.
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  If your email client didn't open, you can manually send an email to <strong className="text-white">help@zeynsoft.com</strong>.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-primary hover:text-primary/80 font-semibold transition"
                >
                  ← Submit Another Request
                </button>
              </div>
            )}
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-white">Frequently Asked Questions</h2>

            <div className="space-y-4">
              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">How do I get started with ZEPHYR AI?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Download ZEPHYR AI from the App Store, create an account, and you can immediately start editing photos with our AI-powered tools. No prior experience is required.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">How do I manage or cancel my subscription?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Subscriptions are managed through your Apple ID. Go to <strong>Settings → [Your Name] → Subscriptions</strong> on your iOS device to view, modify, or cancel your ZEPHYR AI subscription at any time.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">Can I request a refund?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Refund requests for App Store purchases are handled by Apple. Visit <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 transition">reportaproblem.apple.com</a> to request a refund directly from Apple.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">The app crashed or is not working properly. What should I do?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Try the following steps: (1) Close and reopen the app, (2) Make sure you have the latest version installed, (3) Restart your device, (4) Check your internet connection. If the issue continues, please contact us with your device model, iOS version, and app version.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">How is my photo data handled?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Your privacy is important to us. Photos are processed securely and are not shared with third parties. For full details, please review our <Link to="/privacy" className="text-primary hover:text-primary/80 transition">Privacy Policy</Link>.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">How can I delete my account?</h3>
                <p className="text-gray-300 leading-relaxed">
                  You can request permanent account deletion from our <Link to="/account-deletion" className="text-primary hover:text-primary/80 transition">Account Deletion</Link> page. All your data will be removed according to our data retention policy.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">Which devices are supported?</h3>
                <p className="text-gray-300 leading-relaxed">
                  ZEPHYR AI supports iPhone and iPad devices running iOS 14.0 or later. For the best experience, we recommend using the latest iOS version.
                </p>
              </div>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2 text-white">I have a feature request or feedback. Where can I share it?</h3>
                <p className="text-gray-300 leading-relaxed">
                  We'd love to hear from you! Use the contact form above with the category "Feature Request" or email us directly at <strong className="text-white">help@zeynsoft.com</strong>.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-white">Helpful Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/privacy" className="bg-dark-800 border border-white/10 rounded-xl p-6 hover:border-primary/50 transition group">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition">Privacy Policy</h3>
                <p className="text-gray-400 text-sm">Learn how we collect, use, and protect your personal data.</p>
              </Link>

              <Link to="/terms" className="bg-dark-800 border border-white/10 rounded-xl p-6 hover:border-primary/50 transition group">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition">Terms of Service</h3>
                <p className="text-gray-400 text-sm">Read the terms and conditions for using ZEPHYR AI.</p>
              </Link>

              <Link to="/account-deletion" className="bg-dark-800 border border-white/10 rounded-xl p-6 hover:border-primary/50 transition group">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition">Account Deletion</h3>
                <p className="text-gray-400 text-sm">Request permanent deletion of your ZEPHYR AI account.</p>
              </Link>

              <a
                href="https://reportaproblem.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-dark-800 border border-white/10 rounded-xl p-6 hover:border-primary/50 transition group"
              >
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition">Apple Refund Center</h3>
                <p className="text-gray-400 text-sm">Request refunds for App Store purchases directly from Apple.</p>
              </a>
            </div>
          </section>

          <section>
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-8 text-center">
              <h2 className="text-3xl font-bold mb-4 text-white">Still Need Help?</h2>
              <p className="text-gray-300 leading-relaxed mb-6 max-w-2xl mx-auto">
                Our support team is ready to assist you. Reach out to us directly and we'll get back to you as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:help@zeynsoft.com"
                  className="inline-block bg-primary hover:bg-primary/90 text-white font-bold px-8 py-4 rounded-lg transition transform hover:scale-105"
                >
                  help@zeynsoft.com
                </a>
                <a
                  href="mailto:info@zeynsoft.com"
                  className="inline-block bg-dark-800 border border-white/10 hover:border-primary/50 text-white font-bold px-8 py-4 rounded-lg transition"
                >
                  info@zeynsoft.com
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>

      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>© {new Date().getFullYear()} ZEPHYR AI by Zeynsoft. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
